import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import anime from 'animejs/lib/anime.es.js';
import { Instagram, ExternalLink, Music, ChevronLeft, Globe } from 'lucide-react';
import { MagneticLink } from '../components/MagneticButton';

const TEX_HERO = 'https://customer-assets.emergentagent.com/job_ac09e30e-ff84-4be5-9a8e-38b36c255d06/artifacts/kq1h86ag_download.jpg';
const VVS_LOGO = 'https://customer-assets.emergentagent.com/job_ac09e30e-ff84-4be5-9a8e-38b36c255d06/artifacts/yqc71nna_FromNow2.jpg';

// Section separator with gradient fade
function Separator() {
  return (
    <div style={{
      width: '100%',
      height: '1px',
      background: 'linear-gradient(90deg, transparent 0%, rgba(0,180,220,0.12) 30%, rgba(0,180,220,0.12) 70%, transparent 100%)',
    }} />
  );
}

// Caption label
function SectionLabel({ children }) {
  return (
    <p style={{
      fontFamily: 'Space Grotesk, sans-serif',
      fontSize: '10px',
      letterSpacing: '0.22em',
      textTransform: 'uppercase',
      color: 'var(--vvs-cyan)',
      marginBottom: '12px',
    }}>
      {children}
    </p>
  );
}

// YouTube Embed component
function YouTubeEmbed({ videoId, title }) {
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      paddingBottom: '56.25%', // 16:9 ratio
      marginBottom: '16px',
      borderRadius: '4px',
      overflow: 'hidden',
      border: '1px solid rgba(240,244,248,0.06)',
    }}>
      <iframe
        src={`https://www.youtube.com/embed/${videoId}`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          border: 'none',
        }}
      />
    </div>
  );
}

// SoundCloud Embed component
function SoundCloudEmbed({ playlistUrl, title }) {
  const encodedUrl = encodeURIComponent(playlistUrl);
  
  return (
    <div style={{
      marginBottom: '16px',
      borderRadius: '4px',
      overflow: 'hidden',
      border: '1px solid rgba(240,244,248,0.06)',
    }}>
      <iframe
        width="100%"
        height="300"
        scrolling="no"
        frameBorder="no"
        allow="autoplay"
        src={`https://w.soundcloud.com/player/?url=${encodedUrl}&color=%2300b4ff&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=true`}
        title={title}
        style={{ border: 'none' }}
      />
    </div>
  );
}

// Instagram Embed component (simplified - opens in new tab)
function InstagramLink({ reelUrl, label }) {
  return (
    <a
      href={reelUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        fontFamily: 'Space Grotesk, sans-serif',
        fontSize: '12px',
        letterSpacing: '0.06em',
        color: 'var(--vvs-cyan)',
        textDecoration: 'none',
        opacity: 0.85,
        transition: 'opacity 0.25s',
        marginBottom: '8px',
      }}
      onMouseEnter={e => e.currentTarget.style.opacity = '1'}
      onMouseLeave={e => e.currentTarget.style.opacity = '0.85'}
    >
      <Instagram size={14} strokeWidth={1.5} />
      {label}
    </a>
  );
}

// Project link button
function ProjectLink({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        fontFamily: 'Space Grotesk, sans-serif',
        fontSize: '12px',
        letterSpacing: '0.06em',
        color: 'var(--vvs-cyan)',
        textDecoration: 'none',
        opacity: 0.85,
        transition: 'opacity 0.25s',
        marginBottom: '4px',
      }}
      onMouseEnter={e => e.currentTarget.style.opacity = '1'}
      onMouseLeave={e => e.currentTarget.style.opacity = '0.85'}
    >
      <ExternalLink size={11} strokeWidth={1.5} />
      {children}
    </a>
  );
}

// Gig entry with optional YouTube embed
function GigEntry({ name, note, youtubeId }) {
  return (
    <div style={{ padding: '10px 0', borderBottom: '1px solid rgba(240,244,248,0.03)' }}>
      <div style={{ display: 'flex', gap: '16px' }}>
        <div style={{ flex: 1 }}>
          <p style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#F0F4F8', margin: '0 0 3px' }}>
            {name}
          </p>
          {note && <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '12px', lineHeight: 1.7, color: 'var(--vvs-muted)', margin: 0 }}>{note}</p>}
        </div>
      </div>
      {youtubeId && (
        <div style={{ marginTop: '16px' }}>
          <YouTubeEmbed videoId={youtubeId} title={name} />
        </div>
      )}
    </div>
  );
}

// Collab card
function CollabCard({ name, role, url, urlLabel }) {
  return (
    <div style={{
      padding: '20px 24px',
      background: 'rgba(8,16,30,0.5)',
      border: '1px solid rgba(100,160,200,0.06)',
      borderRadius: '4px',
    }}>
      <p style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '15px', textTransform: 'uppercase', color: '#F0F4F8', margin: '0 0 4px', letterSpacing: '0.04em' }}>{name}</p>
      <p style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: '12px', color: 'var(--vvs-muted)', margin: '0 0 10px', lineHeight: 1.6 }}>{role}</p>
      {url && (
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontFamily: 'Space Grotesk', fontSize: '11px', color: 'var(--vvs-cyan)', textDecoration: 'none', opacity: 0.8, transition: 'opacity 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.opacity = '1'}
          onMouseLeave={e => e.currentTarget.style.opacity = '0.8'}
        >
          <ExternalLink size={10} /> {urlLabel}
        </a>
      )}
    </div>
  );
}

export default function ArtistFromNow() {
  const heroRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    document.title = 'fromnow // VVS';
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || !heroRef.current) return;

    anime.timeline({ easing: 'easeOutExpo' })
      .add({ targets: '.fn-back', translateX: [-20, 0], opacity: [0, 1], duration: 600 })
      .add({ targets: '.fn-caption', translateY: [20, 0], opacity: [0, 1], duration: 600 }, '-=300')
      .add({ targets: '.fn-name', translateY: [70, 0], opacity: [0, 1], duration: 1100 }, '-=400')
      .add({ targets: '.fn-bio p', translateY: [20, 0], opacity: [0, 1], duration: 600, delay: anime.stagger(80) }, '-=400')
      .add({ targets: '.fn-roles', translateY: [16, 0], opacity: [0, 1], duration: 500 }, '-=400')
      .add({ targets: '.fn-links a', translateY: [12, 0], opacity: [0, 1], duration: 400, delay: anime.stagger(60) }, '-=300');
  }, []);

  const GIGS_BY_YEAR = [
    {
      year: '2026',
      entries: [
        { name: 'WITHIN // Brussels Planetarium', note: 'February 26, 2026. 360° immersive audiovisual experience. Spatial sound. Dome projection. The most ambitious thing he has ever built.' },
        { name: 'Traxi', note: 'February 2026. Featured as a reference Belgian producer in electronic music.', youtubeId: 'I9FM8XOTtVA' },
      ],
    },
    {
      year: '2025',
      entries: [
        { name: 'Urban Echoes // The Vision Of Ash Bloom at La Cabane', note: 'October 2025.' },
        { name: 'Fabriek Electronique 3', note: 'August 2025. A set built almost entirely from his own compositions // Welcome In The Big Bath, Heatwaves, Strong Swing High, Fuck Money Trees. The set was filmed in full and released in January 2026.', youtubeId: '5wDE2vWb03w' },
        { name: 'VVS x Metro Club', note: 'June 27, 2025. The founding event of Vague Vision Sound. 25 artists. 2 rooms. B2B only. No headliners. One vision.' },
        { name: 'Bruzz Live Session // Frequency by Waro', note: 'May 19, 2025. Live session and interview broadcast on Bruzz.' },
        { name: 'International Music Summit', note: 'April 2025. Surrounded by professionals from across the global music industry.' },
        { name: 'Metro Club Ath', note: 'April 2025. Two hours of solo drive, then an unplanned B2B with Bellebiche.' },
        { name: 'Rock\'n\'Trolls Festival', note: 'April 12.' },
        { name: 'Urban Echoes // First Big Party at Eliane Peniche', note: 'February 28. The collective\'s first major event. A statement.' },
        { name: 'Tears Brussels // Madame Moustache + 666 Klub (Zoo Club)', note: 'February 21, 2025. Two nights in one.' },
        { name: 'Maison Folie Mons', note: 'February 9. For Axial Collective.' },
        { name: 'Paramour Brussels', note: 'February 8. B2B w// LESD for Britney Market Belgium.' },
      ],
    },
    {
      year: '2024',
      entries: [
        { name: 'B2B Marathon', note: 'December 2024 // 4 gigs in Belgium in under a month: Hype x Dark Light w/ Matoz, Momento // Le Télégraphe, Ath, B2B w/ Bellebiche // 666 Klub (Zoo Club) at RN50 Tournai, B2B w/ Boru // Barrio.' },
        { name: 'Hype x Doudou', note: '2024.' },
        { name: 'Jungle Bar Brussels', note: 'Afterparty at 10am for Reckless Social Club.' },
        { name: 'Esperanzah Festival', note: 'First festival appearance.' },
        { name: 'Tears Party // Bar Rouge', note: 'February 2024.' },
      ],
    },
    {
      year: '2023',
      entries: [
        { name: 'Tears Pop Up', note: 'April 2023. Archive Sale Designer + Afterparty, Loft Brussels, Rue de Namur, Brussels.' },
        { name: 'Loft Brussels', note: 'Afterparty before Lancey, for Mo9nde.' },
      ],
    },
    {
      year: '2022',
      entries: [
        { name: 'Tears Pop Up', note: 'August 2022. Archive Sale Designer + Afterparty, 38 Rue de la Violette, Brussels.' },
        { name: 'Lotto Mons Expo', note: '2022. Mons.' },
        { name: 'Coupe de Mons // E//', note: 'A live passage from Techno to Trap with zero warning.' },
      ],
    },
  ];

  return (
    <div style={{ position: 'relative', zIndex: 1, paddingTop: '64px' }}>

      {/* ─── HERO ─── */}
      <section ref={heroRef} style={{ minHeight: isMobile ? 'auto' : '100vh', position: 'relative', display: 'flex', alignItems: 'flex-end', overflow: 'hidden', paddingBottom: isMobile ? '60px' : '0' }}>
        <img src={TEX_HERO} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.22 }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(160deg, rgba(0,30,80,0.3) 0%, transparent 45%, rgba(5,10,20,0.75) 100%)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '65%', background: 'linear-gradient(to top, #050A14 0%, transparent 100%)' }} />
        {!isMobile && <img src={VVS_LOGO} alt="" className="watermark" style={{ width: '160px', right: '5%', top: '25%' }} />}

        <div style={{ position: 'relative', padding: isMobile ? '60px 20px' : '80px 48px', width: '100%', maxWidth: '920px' }}>
          <Link
            to="/artists"
            className="fn-back"
            data-testid="fromnow-back"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: 'var(--vvs-muted)', textDecoration: 'none', fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase', fontFamily: 'Space Grotesk', marginBottom: '32px', opacity: 0, transition: 'color 0.3s' }}
            onMouseEnter={e => e.currentTarget.style.color = '#F0F4F8'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--vvs-muted)'}
          >
            <ChevronLeft size={14} /> Artists
          </Link>

          <p className="fn-caption" style={{ fontFamily: 'Space Grotesk', fontSize: '11px', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--vvs-cyan)', marginBottom: '16px', opacity: 0 }}>
            Founder · Producer · DJ
          </p>

          <h1 className="fn-name" style={{
            fontFamily: 'Syne, sans-serif', fontWeight: 800,
            fontSize: isMobile ? '52px' : 'clamp(72px, 14vw, 156px)',
            lineHeight: 0.85, letterSpacing: '-0.04em', textTransform: 'lowercase',
            color: '#F0F4F8', margin: '0 0 24px', opacity: 0,
          }}>
            fromnow
          </h1>

          <div className="fn-bio" style={{ maxWidth: '600px', marginBottom: '24px' }}>
            {[
              'Passionate about music since the age of 8, when his cousin handed him a burned CD of his first DAW, fromnow never stopped creating. Through projects like Immanence, Ascendance, WAVy and Wave Explorer, he built an artistic universe rooted in movement, emotion and the refusal to be categorized.',
              'Then everything was stolen. His bag, his hard drives, his laptop. 18 years of work, gone in a moment. Every project. Every preset. Every unfinished idea.',
              'But fromnow went looking. Through old emails, forgotten cloud folders, archived SoundCloud drafts. He pulled back what remained // 31 fragments // and made something out of them. FromZero. Not a look back. A point of departure.',
              'Because creating was never optional. It was survival.',
              'Driven by the same question that haunts every independent artist // how do you build something real in a system that wasn\'t made for you? // fromnow studied Finance and Risk Management not out of passion for numbers, but out of a need to understand the machine. That understanding led, alongside Julien // developer, DJ, producer and artist known as Bonoob // to the creation of FairFlow. A tool born from lived experience. A structure built so artists never have to choose between their art and the world.',
            ].map((para, i) => (
              <p key={i} style={{
                fontFamily: 'Space Grotesk, sans-serif',
                fontSize: isMobile ? '13px' : 'clamp(13px, 1.5vw, 15px)',
                lineHeight: 1.9,
                color: i === 3 ? 'var(--vvs-steel)' : 'var(--vvs-muted)',
                fontStyle: i === 3 ? 'italic' : 'normal',
                marginBottom: '14px',
                opacity: 0,
              }}>
                {para}
              </p>
            ))}
          </div>

          <p className="fn-roles" style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: '12px',
            letterSpacing: '0.1em',
            color: 'rgba(200,220,240,0.6)',
            marginBottom: '28px',
            opacity: 0,
          }}>
            Mixing & mastering engineer · Founder of Vague Vision Sound · Co-founder of FairFlow
          </p>

          <div className="fn-links" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {[
              { href: 'https://www.instagram.com/fromnowmusic/', label: 'Instagram', icon: <Instagram size={13} />, testid: 'fromnow-instagram' },
              { href: 'https://soundcloud.com/fromnowproductions', label: 'SoundCloud', icon: <Music size={13} />, testid: 'fromnow-soundcloud' },
              { href: 'https://hobs.space/fromnow/', label: 'Hobs Space', icon: <Globe size={13} />, testid: 'fromnow-hobsspace' },
              { href: 'https://fairflow.be', label: 'FairFlow', icon: <ExternalLink size={13} />, testid: 'fromnow-fairflow' },
            ].map(({ href, label, icon, testid }) => (
              <MagneticLink
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-metallic"
                data-testid={testid}
                style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
              >
                {icon} {label}
              </MagneticLink>
            ))}
          </div>
        </div>
      </section>

      <Separator />

      {/* ─── THE PROJECTS ─── */}
      <section style={{ padding: isMobile ? '60px 20px' : '80px 48px', background: 'rgba(5,12,22,0.4)' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? '32px' : 'clamp(40px, 6vw, 56px)', textTransform: 'uppercase', color: '#F0F4F8', marginBottom: '12px', letterSpacing: '-0.02em' }}>
            THE PROJECTS
          </h2>
          <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '15px', letterSpacing: '0.08em', color: 'var(--vvs-muted)', marginBottom: '60px' }}>
            A Discography Built in Concepts
          </p>

          {/* IMMANENCE */}
          <div style={{ marginBottom: '60px', paddingBottom: '60px', borderBottom: '1px solid rgba(240,244,248,0.04)' }}>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? '22px' : '28px', textTransform: 'uppercase', color: '#F0F4F8', margin: '0 0 4px', letterSpacing: '-0.01em' }}>
              IMMANENCE
            </h3>
            <span style={{ fontFamily: 'Space Grotesk', fontSize: '11px', letterSpacing: '0.14em', color: 'var(--vvs-muted)' }}>2021</span>
            <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '15px', lineHeight: 1.85, color: 'var(--vvs-steel)', margin: '16px 0' }}>
              First solo project. A conceptual experiment.
            </p>
            <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '14px', lineHeight: 1.85, color: 'var(--vvs-muted)', marginBottom: '20px' }}>
              fromnow sent 50 handwritten letters to friends scattered across the globe. Inside each envelope: 3 stickers marked "2030" and access to an INA archive // real archival footage discussing what the 2030s might look like. A way to make people think about what's coming before it arrives. 3 unique cassette tapes were hand-crafted for the project. No streaming. No algorithm. Just an object, a letter, and a question about the future we're building together.
            </p>
            <YouTubeEmbed videoId="EDQ7Gme_vgM" title="Immanence - fromnow" />
          </div>

          {/* ASCENDANCE */}
          <div style={{ marginBottom: '60px', paddingBottom: '60px', borderBottom: '1px solid rgba(240,244,248,0.04)' }}>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? '22px' : '28px', textTransform: 'uppercase', color: '#F0F4F8', margin: '0 0 4px', letterSpacing: '-0.01em' }}>
              ASCENDANCE
            </h3>
            <span style={{ fontFamily: 'Space Grotesk', fontSize: '11px', letterSpacing: '0.14em', color: 'var(--vvs-muted)' }}>2022</span>
            <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '15px', lineHeight: 1.85, color: 'var(--vvs-steel)', margin: '16px 0' }}>
              24 tracks. 24 days. A calendar of sound.
            </p>
            <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '14px', lineHeight: 1.85, color: 'var(--vvs-muted)', marginBottom: '16px' }}>
              Released as an Advent calendar // one track per day throughout December. Each day, the water level rose inside the "A" of the word Ascendance on the visual. A slow, inevitable, beautiful flood. The concept was built around waves // both literal and metaphorical. A tribute to what came before us. A symbol of rising waters as the consequence of our choices. All revenue was directed to The SeaCleaners to clean the oceans. Along the way, a collaboration with Hujigramm produced Portal // one of the project's most hypnotic moments. The response was unanimous: cinematic, dramatic, immersive. People said the music felt visual before it even had images. That feedback changed everything.
            </p>
            <YouTubeEmbed videoId="J3YAWX8TGzw" title="Ascendance Outro - fromnow" />
            <YouTubeEmbed videoId="38ZoOHZZypo" title="Ascendance Bonus - fromnow" />
            <div style={{ marginTop: '12px' }}>
              <ProjectLink href="https://tr.ee/o3YP2P0k0S">Full project</ProjectLink>
            </div>
          </div>

          {/* WAVy EP */}
          <div style={{ marginBottom: '60px', paddingBottom: '60px', borderBottom: '1px solid rgba(240,244,248,0.04)' }}>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? '22px' : '28px', textTransform: 'uppercase', color: '#F0F4F8', margin: '0 0 4px', letterSpacing: '-0.01em' }}>
              WAVy EP
            </h3>
            <span style={{ fontFamily: 'Space Grotesk', fontSize: '11px', letterSpacing: '0.14em', color: 'var(--vvs-muted)' }}>2023</span>
            <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '15px', lineHeight: 1.85, color: 'var(--vvs-steel)', margin: '16px 0' }}>
              Made before knowing where he was going.
            </p>
            <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '14px', lineHeight: 1.85, color: 'var(--vvs-muted)', marginBottom: '16px' }}>
              fromnow's mother organized a surprise trip. Destination unknown. He made WAVy before leaving // channeling the exact feeling of excitement toward an unknown horizon. 5 tracks. Pure anticipation turned into sound. He ended up in Thailand. The EP fit perfectly. It felt like a musical retrospection of a future he hadn't lived yet. Don't Bother Me was the most celebrated track. A moment of warmth breaking through his usually colder, more cinematic palette.
            </p>
            <ProjectLink href="https://tr.ee/EoaVa32ar4">Listen</ProjectLink>
          </div>

          {/* WAVE EXPLORER */}
          <div style={{ marginBottom: '60px', paddingBottom: '60px', borderBottom: '1px solid rgba(240,244,248,0.04)' }}>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? '22px' : '28px', textTransform: 'uppercase', color: '#F0F4F8', margin: '0 0 4px', letterSpacing: '-0.01em' }}>
              WAVE EXPLORER
            </h3>
            <span style={{ fontFamily: 'Space Grotesk', fontSize: '11px', letterSpacing: '0.14em', color: 'var(--vvs-muted)' }}>2023–2024</span>
            <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '15px', lineHeight: 1.85, color: 'var(--vvs-steel)', margin: '16px 0' }}>
              A community challenge. One genre at a time.
            </p>
            <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '14px', lineHeight: 1.85, color: 'var(--vvs-muted)', marginBottom: '20px' }}>
              fromnow built a concept around his community: they vote on a genre, he produces it, documents the entire process on Instagram, and releases the result. No safety net. No familiar territory. Just the challenge.
            </p>

            {/* Wave Explorer #1 */}
            <div style={{ paddingLeft: '16px', borderLeft: '1px solid rgba(0,240,255,0.1)', marginBottom: '24px' }}>
              <p style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '14px', textTransform: 'uppercase', color: '#F0F4F8', margin: '0 0 8px', letterSpacing: '0.05em' }}>
                Wave Explorer #1 // FLUX // Genre: Jungle
              </p>
              <ProjectLink href="https://tr.ee/Xt_EyEIwRy">Listen</ProjectLink>
            </div>

            {/* Wave Explorer #2 */}
            <div style={{ paddingLeft: '16px', borderLeft: '1px solid rgba(0,240,255,0.1)' }}>
              <p style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '14px', textTransform: 'uppercase', color: '#F0F4F8', margin: '0 0 8px', letterSpacing: '0.05em' }}>
                Wave Explorer #2 // HOW // Genre: Jazz
              </p>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: '13px', lineHeight: 1.8, color: 'var(--vvs-muted)', marginBottom: '12px' }}>
                A full collaborative production with Dana Shanti on vocals, Aziz on saxophone, El Scinta and Davidheim on drums, Fomobeats on guitar // recorded at Temple Studios. Bonus track Midnight Velvet with Boru and Aziz.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <InstagramLink reelUrl="https://www.instagram.com/reel/C27soyetx92/" label="Watch the making-of" />
                <ProjectLink href="https://tr.ee/vNfYyvHfF9">Listen to the track</ProjectLink>
              </div>
            </div>
          </div>

          {/* INFINI */}
          <div style={{ marginBottom: '60px', paddingBottom: '60px', borderBottom: '1px solid rgba(240,244,248,0.04)' }}>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? '22px' : '28px', textTransform: 'uppercase', color: '#F0F4F8', margin: '0 0 4px', letterSpacing: '-0.01em' }}>
              INFINI
            </h3>
            <span style={{ fontFamily: 'Space Grotesk', fontSize: '11px', letterSpacing: '0.14em', color: 'var(--vvs-muted)' }}>2024</span>
            <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '15px', lineHeight: 1.85, color: 'var(--vvs-steel)', margin: '16px 0' }}>
              A collaboration that opened unexpected doors.
            </p>
            <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '14px', lineHeight: 1.85, color: 'var(--vvs-muted)', marginBottom: '16px' }}>
              Made with Woods.Audio, Infini became more than a release // it became a gateway. fromnow organized a remix competition around the track. The prize: exclusive access to el3ment, an invite-only global artist network based in Los Angeles where you can only enter through another artist's code. 5 winners. Among them: Daigo, who went on to produce for La Fève on two tracks including No Hook.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <ProjectLink href="https://tr.ee/-sscruntPE">Listen</ProjectLink>
              <ProjectLink href="https://tr.ee/1TDrc4Rbmu">Remixes</ProjectLink>
            </div>
          </div>

          {/* MORE RELEASES */}
          <div style={{ marginBottom: '60px', paddingBottom: '60px', borderBottom: '1px solid rgba(240,244,248,0.04)' }}>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? '22px' : '28px', textTransform: 'uppercase', color: '#F0F4F8', margin: '0 0 4px', letterSpacing: '-0.01em' }}>
              MORE RELEASES
            </h3>
            <span style={{ fontFamily: 'Space Grotesk', fontSize: '11px', letterSpacing: '0.14em', color: 'var(--vvs-muted)' }}>2022–2025</span>
            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: '13px', lineHeight: 1.8, color: 'var(--vvs-muted)' }}>
                <strong style={{ color: 'var(--vvs-steel)' }}>Oskar // Shades (fromnow Remix)</strong> // 2022, part of the Fragments EP Remixes
              </p>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: '13px', lineHeight: 1.8, color: 'var(--vvs-muted)' }}>
                <strong style={{ color: 'var(--vvs-steel)' }}>SBK // Survival (fromnow Remix)</strong> // 2024. A remix for British rapper Yung SBK. Mastering by Obridaigo.
              </p>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: '13px', lineHeight: 1.8, color: 'var(--vvs-muted)' }}>
                <strong style={{ color: 'var(--vvs-steel)' }}>Lysjoy // Lokumu Ya Basi (fromnow Remix)</strong> // November 2024. A song in Lingala celebrating "the honour of women," originally composed by Piotr Paluch. Mastering by Chontane. Connected through Nina.
              </p>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: '13px', lineHeight: 1.8, color: 'var(--vvs-muted)' }}>
                <strong style={{ color: 'var(--vvs-steel)' }}>Frozen Waves</strong> // Released as a limited free download in December 2024, then on all platforms in January 2025.
              </p>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: '13px', lineHeight: 1.8, color: 'var(--vvs-muted)' }}>
                <strong style={{ color: 'var(--vvs-steel)' }}>Strong Swing High</strong> // January 2025. Released on DISOSS, a Swiss dub techno label. Mastering by Ereib.
              </p>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: '13px', lineHeight: 1.8, color: 'var(--vvs-muted)' }}>
                <strong style={{ color: 'var(--vvs-steel)' }}>Britof EP Remixes</strong> // September 2025. A remix contribution for Slovenian raw techno label Heimisgaror.
              </p>
            </div>
            <div style={{ marginTop: '16px' }}>
              <ProjectLink href="https://tr.ee/t8CPxZLPvf">SBK // Survival (fromnow Remix)</ProjectLink>
            </div>
          </div>

          {/* FROMZERO I / II / III */}
          <div style={{ marginBottom: '40px' }}>
            <h3 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? '22px' : '28px', textTransform: 'uppercase', color: '#F0F4F8', margin: '0 0 4px', letterSpacing: '-0.01em' }}>
              FROMZERO I // II // III
            </h3>
            <span style={{ fontFamily: 'Space Grotesk', fontSize: '11px', letterSpacing: '0.14em', color: 'var(--vvs-muted)' }}>2025</span>
            <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '15px', lineHeight: 1.85, color: 'var(--vvs-steel)', margin: '16px 0' }}>
              31 fragments saved from silence.
            </p>
            <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '14px', lineHeight: 1.85, color: 'var(--vvs-muted)', marginBottom: '16px' }}>
              Everything was stolen. The hard drives. The laptop. 18 years of archives, gone in one moment. Every project. Every preset. Every unfinished idea. fromnow searched everywhere. Old emails. Cloud folders. Forgotten platforms. Archived SoundCloud drafts. He gathered what remained // raw, unfinished, honest // and released it in three volumes, two weeks apart. Not perfect. Alive.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: '13px', lineHeight: 1.7, color: 'var(--vvs-muted)' }}>
                <strong style={{ color: 'var(--vvs-cyan)' }}>Volume I</strong> // Cold, dubby, tense. Techno in its most brute form.
              </p>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: '12px', lineHeight: 1.7, color: 'var(--vvs-subtle)', fontStyle: 'italic', marginLeft: '16px' }}>
                Two tracks from Volume I // "Amour's Veil" and "I Got All The Syrup" // were mastered by Chontane.
              </p>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: '13px', lineHeight: 1.7, color: 'var(--vvs-muted)' }}>
                <strong style={{ color: 'var(--vvs-cyan)' }}>Volume II</strong> // Introspective, dense, forward-thinking. 11 tracks that go inward before they go anywhere else.
              </p>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: '13px', lineHeight: 1.7, color: 'var(--vvs-muted)' }}>
                <strong style={{ color: 'var(--vvs-cyan)' }}>Volume III</strong> // Light returning. Dub techno textures, UK grooves, Berlin energy. A loop that closes and opens simultaneously.
              </p>
            </div>

            {/* Quote */}
            <div style={{
              marginBottom: '24px',
              padding: '20px 24px',
              borderLeft: '2px solid rgba(0,200,220,0.25)',
              background: 'rgba(0,100,130,0.06)',
            }}>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: '14px', lineHeight: 1.85, color: 'var(--vvs-steel)', fontStyle: 'italic', margin: 0 }}>
                "If someone listens to FromZero and feels less alone in their own fall or renaissance // then I've succeeded."
              </p>
            </div>

            {/* SoundCloud Embeds */}
            <SoundCloudEmbed playlistUrl="https://soundcloud.com/fromnowproductions/sets/fromzero-i" title="FromZero I" />
            <SoundCloudEmbed playlistUrl="https://soundcloud.com/fromnowproductions/sets/vvs002-fromzero-ii" title="FromZero II" />
            <SoundCloudEmbed playlistUrl="https://soundcloud.com/fromnowproductions/sets/fromzero-iii" title="FromZero III" />

            <div style={{ marginTop: '16px' }}>
              <ProjectLink href="https://www.septmille.be/chronique/fromnow-de-la-perte-la-renaissance-linstinct-brut-dun-createur-en-reconstruction">Read the full story // Septmille</ProjectLink>
            </div>
          </div>
        </div>
      </section>

      <Separator />

      {/* ─── LIVE & GIGS ─── */}
      <section style={{ padding: isMobile ? '60px 20px' : '80px 48px' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? '32px' : 'clamp(40px, 6vw, 56px)', textTransform: 'uppercase', color: '#F0F4F8', marginBottom: '12px', letterSpacing: '-0.02em' }}>
            LIVE & GIGS
          </h2>
          <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '15px', letterSpacing: '0.08em', color: 'var(--vvs-muted)', marginBottom: '60px' }}>
            A Selection of Key Moments
          </p>

          {GIGS_BY_YEAR.map(({ year, entries }) => (
            <div key={year} style={{ marginBottom: '48px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginBottom: '16px' }}>
                <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '13px', letterSpacing: '0.18em', color: 'var(--vvs-cyan)' }}>{year}</span>
                <div style={{ flex: 1, height: '1px', background: 'rgba(0,240,255,0.1)' }} />
              </div>
              {entries.map((gig, i) => (
                <GigEntry key={i} name={gig.name} note={gig.note} youtubeId={gig.youtubeId} />
              ))}
            </div>
          ))}
        </div>
      </section>

      <Separator />

      {/* ─── COLLABS & OPPORTUNITIES ─── */}
      <section style={{ padding: isMobile ? '60px 20px' : '80px 48px', background: 'rgba(5,12,22,0.4)' }}>
        <div style={{ maxWidth: '860px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? '32px' : 'clamp(36px, 5vw, 48px)', textTransform: 'uppercase', color: '#F0F4F8', marginBottom: '12px', letterSpacing: '-0.02em' }}>
            COLLABS & OPPORTUNITIES
          </h2>
          <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '15px', letterSpacing: '0.08em', color: 'var(--vvs-muted)', marginBottom: '48px' }}>
            Connections
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
            <CollabCard name="Playtronica" role="Hardware partnership." url="https://instagram.com/playtronica" urlLabel="@playtronica" />
            <CollabCard name="Dimitri Andreas" role="Artistic partnership." url="https://instagram.com/dimitriandreasmusic" urlLabel="@dimitriandreasmusic" />
            <CollabCard name="el3ment // Los Angeles" role="Invite-only global artist community." url="https://instagram.com/_el3ment" urlLabel="@_el3ment" />
            <CollabCard name="DISOSS // Switzerland" role="Swiss dub techno label, released Strong Swing High." url="https://soundcloud.com/disoss_collective" urlLabel="SoundCloud" />
            <CollabCard name="Heimisgaror // Slovenia" role="Raw techno label, Britof EP remix." />
            <CollabCard name="Traxi" role="Featured artist and producer." />
            <CollabCard name="International Music Summit" role="Official participant." />
          </div>
        </div>
      </section>

      <Separator />

      {/* ─── MEDIA & PRESS ─── */}
      <section style={{ padding: isMobile ? '60px 20px' : '80px 48px' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? '32px' : 'clamp(36px, 5vw, 48px)', textTransform: 'uppercase', color: '#F0F4F8', marginBottom: '12px', letterSpacing: '-0.02em' }}>
            MEDIA & PRESS
          </h2>
          <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '15px', letterSpacing: '0.08em', color: 'var(--vvs-muted)', marginBottom: '48px' }}>
            Coverage
          </p>

          {[
            { outlet: 'Urban Echoes // POD#011', date: 'August 2025', desc: 'Podcast mix published by Urban Echoes.', url: 'https://instagram.com/urban.echoes__', urlLabel: '@urban.echoes__' },
            { outlet: 'Frequency by Waro / Bruzz', date: 'June 2025', desc: 'Live session and interview, rebroadcast June 2025.' },
            { outlet: 'Traxi', date: 'February 2026', desc: 'Featured as a reference Belgian producer in electronic music.' },
            { outlet: 'Septmille', date: 'April 2025', desc: 'Full chronicle on FromZero.', url: 'https://www.septmille.be/chronique/fromnow-de-la-perte-la-renaissance-linstinct-brut-dun-createur-en-reconstruction', urlLabel: 'Read the article' },
          ].map((item, i) => (
            <div key={i} style={{
              padding: '24px 0',
              borderBottom: '1px solid rgba(240,244,248,0.04)',
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr auto',
              gap: isMobile ? '8px' : '24px',
              alignItems: 'start',
            }}>
              <div>
                <p style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '16px', textTransform: 'uppercase', color: '#F0F4F8', margin: '0 0 4px', letterSpacing: '0.02em' }}>{item.outlet}</p>
                <p style={{ fontFamily: 'Space Grotesk', fontSize: '13px', lineHeight: 1.75, color: 'var(--vvs-muted)', margin: '0 0 8px' }}>{item.desc}</p>
                {item.url && <ProjectLink href={item.url}>{item.urlLabel}</ProjectLink>}
              </div>
              <span style={{ fontFamily: 'Space Grotesk', fontSize: '11px', letterSpacing: '0.1em', color: 'var(--vvs-subtle)', whiteSpace: 'nowrap', paddingTop: '2px' }}>{item.date}</span>
            </div>
          ))}
        </div>
      </section>

      <Separator />

      {/* ─── BEYOND THE MUSIC ─── */}
      <section style={{ padding: isMobile ? '60px 20px' : '80px 48px', background: 'rgba(5,12,22,0.5)' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? '32px' : 'clamp(36px, 5vw, 48px)', textTransform: 'uppercase', color: '#F0F4F8', marginBottom: '12px', letterSpacing: '-0.02em' }}>
            BEYOND THE MUSIC
          </h2>
          <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '15px', letterSpacing: '0.08em', color: 'var(--vvs-muted)', marginBottom: '36px' }}>
            The Bigger Picture
          </p>
          {[
            'fromnow is a mixing and mastering engineer with an active client base across Belgium and beyond. He works with Temple Studios and from his own setup: Adam Audio monitors, audio interface, MIDI piano. All genres accepted.',
            'He studied Finance and Risk Management // not to leave music, but to understand how to protect it. That path led to FairFlow, co-founded with Julien (Bonoob).',
            'He is the founder of Vague Vision Sound and the driving force behind WITHIN // an immersive 360° audiovisual show at the Brussels Planetarium, February 26, 2026.',
          ].map((para, i) => (
            <p key={i} style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '14px' : 'clamp(14px, 1.6vw, 16px)', lineHeight: 2, color: 'var(--vvs-steel)', marginBottom: '16px' }}>
              {para}
            </p>
          ))}
        </div>
      </section>

      <Separator />

      {/* ─── LINKS ─── */}
      <section style={{ padding: isMobile ? '60px 20px 100px' : '80px 48px 120px' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: isMobile ? '32px' : 'clamp(36px, 5vw, 48px)', textTransform: 'uppercase', color: '#F0F4F8', marginBottom: '12px', letterSpacing: '-0.02em' }}>
            LINKS
          </h2>
          <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '13px' : '15px', letterSpacing: '0.08em', color: 'var(--vvs-muted)', marginBottom: '40px' }}>
            Find fromnow
          </p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {[
              { href: 'https://instagram.com/fromnowmusic', icon: <Instagram size={14} />, label: 'Instagram', testid: 'fromnow-link-instagram' },
              { href: 'https://soundcloud.com/fromnowproductions', icon: <Music size={14} />, label: 'SoundCloud', testid: 'fromnow-link-soundcloud' },
              { href: 'https://hobs.space/fromnow/', icon: <Globe size={14} />, label: 'Hobs Space', testid: 'fromnow-link-hobs' },
              { href: 'https://fairflow.be', icon: <ExternalLink size={14} />, label: 'FairFlow', testid: 'fromnow-link-fairflow' },
            ].map(({ href, icon, label, testid }) => (
              <MagneticLink
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-metallic btn-metallic-filled"
                data-testid={testid}
                style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '10px', fontSize: '12px', letterSpacing: '0.12em' }}
              >
                {icon} {label}
              </MagneticLink>
            ))}
          </div>

          <div style={{ marginTop: '60px', display: 'flex', justifyContent: 'flex-start' }}>
            <Link to="/artists" className="btn-metallic" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '11px' }} data-testid="fromnow-back-bottom">
              <ChevronLeft size={13} /> Back to Artists
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
